import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, Building2, Phone, MapPin, Users, Briefcase, Save, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { getOrCreateCompany, updateUserPhone, updateCompanyProfile, fetchUserProfile } from "@/services/diagnosticService";
import { toast } from "sonner";

const SIZE_OPTIONS = [
  { value: "", label: "Sélectionner..." },
  { value: "1-10", label: "1 à 10 salariés" },
  { value: "11-50", label: "11 à 50 salariés" },
  { value: "51-200", label: "51 à 200 salariés" },
  { value: "201-500", label: "201 à 500 salariés" },
  { value: "500+", label: "Plus de 500 salariés" },
];

const INDUSTRY_OPTIONS = [
  { value: "", label: "Sélectionner..." },
  { value: "commerce", label: "Commerce & Distribution" },
  { value: "services", label: "Services aux entreprises" },
  { value: "industrie", label: "Industrie & Manufacturing" },
  { value: "tech", label: "Technologie & IT" },
  { value: "sante", label: "Santé & Médical" },
  { value: "finance", label: "Finance & Assurance" },
  { value: "immobilier", label: "Immobilier & Construction" },
  { value: "transport", label: "Transport & Logistique" },
  { value: "restauration", label: "Hôtellerie & Restauration" },
  { value: "autre", label: "Autre" },
];

export default function ProfilPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);

  // Form state
  const [phone, setPhone] = useState("");
  const [industry, setIndustry] = useState("");
  const [sizeRange, setSizeRange] = useState("");
  const [city, setCity] = useState("");

  // Track initial values to detect changes
  const [initialValues, setInitialValues] = useState({});

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/connexion?redirect=/mon-profil");
    }
  }, [user, authLoading, navigate]);

  // Load user profile and company data
  useEffect(() => {
    if (!user) return;

    async function loadData() {
      setLoading(true);
      try {
        // Fetch phone from public.users table
        const userProfile = await fetchUserProfile(user.id);
        
        // Get or create company
        const companyData = await getOrCreateCompany(user.id);
        setCompany(companyData);

        // Set form values - phone from users table, not auth.user()
        const phoneValue = userProfile?.phone || "";
        const industryValue = companyData?.industry || "";
        const sizeValue = companyData?.size_range || "";
        const cityValue = companyData?.city || "";

        setPhone(phoneValue);
        setIndustry(industryValue);
        setSizeRange(sizeValue);
        setCity(cityValue);

        setInitialValues({
          phone: phoneValue,
          industry: industryValue,
          sizeRange: sizeValue,
          city: cityValue,
        });
      } catch (error) {
        console.error("Erreur lors du chargement du profil:", error);
        toast.error("Erreur lors du chargement du profil");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [user]);

  // Check if there are unsaved changes
  const hasChanges = () => {
    return (
      phone !== initialValues.phone ||
      industry !== initialValues.industry ||
      sizeRange !== initialValues.sizeRange ||
      city !== initialValues.city
    );
  };

  // Handle save
  const handleSave = async () => {
    if (!user || !company) return;

    setSaving(true);
    try {
      // Update phone if changed
      if (phone !== initialValues.phone) {
        await updateUserPhone(user.id, phone || null);
      }

      // Update company fields if changed
      const companyUpdates = {};
      if (industry !== initialValues.industry) companyUpdates.industry = industry || null;
      if (sizeRange !== initialValues.sizeRange) companyUpdates.size_range = sizeRange || null;
      if (city !== initialValues.city) companyUpdates.city = city || null;

      if (Object.keys(companyUpdates).length > 0) {
        await updateCompanyProfile(company.id, companyUpdates);
      }

      // Update initial values
      setInitialValues({
        phone,
        industry,
        sizeRange,
        city,
      });

      toast.success("Profil mis à jour avec succès");
    } catch (error) {
      console.error("Erreur lors de la sauvegarde:", error);
      toast.error(error.message || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#59C3A6]" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div data-testid="profil-page" className="min-h-screen pt-28 pb-20 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/mon-espace")}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={18} />
            Retour à mon espace
          </button>
          <h1 className="text-3xl font-bold text-white mb-2">Mon profil</h1>
          <p className="text-slate-400">Gérez vos informations personnelles et celles de votre entreprise</p>
        </div>

        {/* Profile Form */}
        <div className="space-y-8">
          {/* Personal Information */}
          <div className="bg-[#0F2847]/50 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#59C3A6]/20 rounded-xl flex items-center justify-center">
                <User size={20} className="text-[#59C3A6]" />
              </div>
              <h2 className="text-xl font-semibold text-white">Informations personnelles</h2>
            </div>

            <div className="space-y-4">
              {/* Email (read-only) */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Email
                </label>
                <div className="relative">
                  <input
                    type="email"
                    data-testid="profil-email"
                    value={user.email || ""}
                    disabled
                    className="w-full px-4 py-3 bg-[#0B1F3B]/50 border border-white/10 rounded-xl text-slate-400 cursor-not-allowed"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">
                    Lecture seule
                  </span>
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Phone size={14} className="inline mr-2" />
                  Téléphone
                </label>
                <input
                  type="tel"
                  data-testid="profil-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+33 6 12 34 56 78"
                  className="w-full px-4 py-3 bg-[#0B1F3B] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] transition-all outline-none"
                />
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="bg-[#0F2847]/50 border border-white/10 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#59C3A6]/20 rounded-xl flex items-center justify-center">
                <Building2 size={20} className="text-[#59C3A6]" />
              </div>
              <h2 className="text-xl font-semibold text-white">Informations entreprise</h2>
            </div>

            <div className="space-y-4">
              {/* Industry */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Briefcase size={14} className="inline mr-2" />
                  Secteur d'activité
                </label>
                <select
                  data-testid="profil-industry"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0B1F3B] border border-white/10 rounded-xl text-white focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] transition-all outline-none appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                    backgroundSize: "1.5em 1.5em",
                  }}
                >
                  {INDUSTRY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Size Range */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <Users size={14} className="inline mr-2" />
                  Taille de l'entreprise
                </label>
                <select
                  data-testid="profil-size"
                  value={sizeRange}
                  onChange={(e) => setSizeRange(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0B1F3B] border border-white/10 rounded-xl text-white focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] transition-all outline-none appearance-none cursor-pointer"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 1rem center",
                    backgroundSize: "1.5em 1.5em",
                  }}
                >
                  {SIZE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  <MapPin size={14} className="inline mr-2" />
                  Ville
                </label>
                <input
                  type="text"
                  data-testid="profil-city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Paris, Lyon, Marseille..."
                  className="w-full px-4 py-3 bg-[#0B1F3B] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#59C3A6] focus:ring-1 focus:ring-[#59C3A6] transition-all outline-none"
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              data-testid="profil-save-btn"
              onClick={handleSave}
              disabled={!hasChanges() || saving}
              className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 ${
                hasChanges()
                  ? "bg-[#59C3A6] text-[#0B1F3B] hover:bg-[#4AB095] shadow-[0_0_20px_rgba(89,195,166,0.25)]"
                  : "bg-slate-700 text-slate-400 cursor-not-allowed"
              }`}
            >
              {saving ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save size={18} />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
